import { useMemo } from 'react';
import {
  useSearchConversationsTotalCountQuery,
  useSearchSourcingCandidatesTotalCountQuery,
  useSearchSourcingCandidatesQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  ConversationStatus,
  SourcedCandidateStatus
} from 'generated-graphql/types';
import { calculatePercentage } from 'utils/calculatePercentage';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

type UseSourcingStatsProps = {
  candidateBatchId?: string;
  organizationId: string;
  totalSourcedCandidates: number;
};

type SourcingStatsData = {
  appliedCount: number;
  deliveredPercentage: number;
  respondedPercentage: number;
  notInterestedPercentage: number;
  optedOutPercentage: number;
  isLoading: boolean;
};

type StatsCount = {
  delivered: number;
  responded: number;
  notInterested: number;
  optedOut: number;
};

const calculateStatsPercentages = (
  counts: StatsCount,
  totalSourcedCandidates: number
): Omit<SourcingStatsData, 'appliedCount' | 'isLoading'> => {
  const { delivered, responded, notInterested, optedOut } = counts;

  return {
    deliveredPercentage:
      delivered > 0
        ? calculatePercentage(delivered, totalSourcedCandidates)
        : 0,
    respondedPercentage:
      delivered > 0 ? calculatePercentage(responded, delivered) : 0,
    notInterestedPercentage:
      delivered > 0 ? calculatePercentage(notInterested, delivered) : 0,
    optedOutPercentage:
      delivered > 0 ? calculatePercentage(optedOut, delivered) : 0
  };
};

export const useSourcingStats = ({
  candidateBatchId,
  organizationId,
  totalSourcedCandidates
}: UseSourcingStatsProps): SourcingStatsData => {
  // When candidateBatchId is present, we need to fetch sourced candidates
  // and calculate metrics client-side since candidateBatchId doesn't exist
  // as a filter on searchConversationFilter
  const { data: sourcedCandidatesData, loading: sourcedCandidatesLoading } =
    useSearchSourcingCandidatesQuery({
      variables: {
        filter: {
          and: [
            { status: { ne: SourcedCandidateStatus.AwaitingEnrichment } },
            { candidateBatchId: { eq: candidateBatchId } },
            { organizationId: { eq: organizationId } }
          ]
        },
        limit: 10000
      },
      skip: !candidateBatchId || !organizationId
    });

  const baseConversationFilter = useMemo(
    () => ({
      and: [
        { organizationId: { eq: organizationId } },
        { sourcedCandidateId: { exists: true } }
      ]
    }),
    [organizationId]
  );

  const { data: appliedCandidatesData, loading: appliedCandidatesLoading } =
    useSearchSourcingCandidatesTotalCountQuery({
      variables: {
        filter: {
          and: [
            ...(candidateBatchId
              ? [{ candidateBatchId: { eq: candidateBatchId } }]
              : []),
            { organizationId: { eq: organizationId } },
            { candidateId: { exists: true } }
          ]
        }
      },
      skip: !organizationId
    });

  // Only use conversation queries when candidateBatchId is not present
  const { data: deliveredData, loading: deliveredLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseConversationFilter,
          sentAt: { exists: true }
        }
      },
      skip: !organizationId || !!candidateBatchId
    });

  const { data: respondedData, loading: respondedLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseConversationFilter,
          sentAt: { exists: true },
          repliesCount: { gt: 0 }
        }
      },
      skip: !organizationId || !!candidateBatchId
    });

  const { data: notInterestedData, loading: notInterestedLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseConversationFilter,
          sentAt: { exists: true },
          or: [{ status: { eq: ConversationStatus.CompletedNotInterested } }]
        }
      },
      skip: !organizationId || !!candidateBatchId
    });

  const { data: optedOutData, loading: optedOutLoading } =
    useSearchConversationsTotalCountQuery({
      variables: {
        filter: {
          ...baseConversationFilter,
          sentAt: { exists: true },
          status: { eq: ConversationStatus.OptedOut }
        }
      },
      skip: !organizationId || !!candidateBatchId
    });

  const isLoading = useMemo(() => {
    // When candidateBatchId is present, we only need to wait for sourced candidates and applied candidates
    if (candidateBatchId) {
      return sourcedCandidatesLoading || appliedCandidatesLoading;
    }

    // Otherwise, wait for all conversation queries
    return (
      appliedCandidatesLoading ||
      deliveredLoading ||
      respondedLoading ||
      notInterestedLoading ||
      optedOutLoading
    );
  }, [
    candidateBatchId,
    sourcedCandidatesLoading,
    appliedCandidatesLoading,
    deliveredLoading,
    respondedLoading,
    notInterestedLoading,
    optedOutLoading
  ]);

  return useMemo(() => {
    const appliedCount =
      appliedCandidatesData?.searchSourcedCandidates?.total ?? 0;

    if (candidateBatchId && sourcedCandidatesData) {
      const candidates = extractQueryListItems(sourcedCandidatesData);

      const deliveredCandidates = candidates.filter(
        (candidate) => candidate.conversation?.sentAt
      );

      const counts: StatsCount = {
        delivered: deliveredCandidates.length,
        responded: deliveredCandidates.filter(
          (candidate) => (candidate.conversationRepliesCount ?? 0) > 0
        ).length,
        notInterested: deliveredCandidates.filter(
          (candidate) =>
            candidate.conversationStatus ===
            ConversationStatus.CompletedNotInterested
        ).length,
        optedOut: deliveredCandidates.filter(
          (candidate) =>
            candidate.conversationStatus === ConversationStatus.OptedOut
        ).length
      };

      return {
        appliedCount,
        ...calculateStatsPercentages(counts, totalSourcedCandidates),
        isLoading
      };
    }

    const counts: StatsCount = {
      delivered: deliveredData?.searchConversations?.total ?? 0,
      responded: respondedData?.searchConversations?.total ?? 0,
      notInterested: notInterestedData?.searchConversations?.total ?? 0,
      optedOut: optedOutData?.searchConversations?.total ?? 0
    };

    return {
      appliedCount,
      ...calculateStatsPercentages(counts, totalSourcedCandidates),
      isLoading
    };
  }, [
    appliedCandidatesData,
    candidateBatchId,
    sourcedCandidatesData,
    deliveredData,
    respondedData,
    notInterestedData,
    optedOutData,
    totalSourcedCandidates,
    isLoading
  ]);
};
